import React, { useEffect, useState, useContext } from 'react';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import CloseIcon from '@videoblocks/react-icons/Close';
import TimeClockCircle from '@videoblocks/react-icons/TimeClockCircle';
import { getSearchUrl } from '@videoblocks/shared-components/dist/mjs/components/Shared/SearchUtils';

import { searchOrigins } from '../../../../Navigation/constants';
import { SearchFilterContentTypes } from '../../../../Shared/enums';
import { NavSearchContext } from '../NavSearchContext';
import { SearchBarVariant } from '../NavSearchElements/searchUtils';
import {
  SearchEntry,
  SearchHistoryType,
  getContentClassByContentType,
} from './util';

interface SearchHistoryProps {
  contentType: SearchFilterContentTypes;
  children: JSX.Element;
}

const searchBarVariantStyle = {
  [SearchBarVariant.ProminentSearch]: 'w-96 py-2',
  [SearchBarVariant.TopNav]: 'py-2 w-500px',
};

function SearchHistory({ contentType, children }: SearchHistoryProps) {
  const context = useContext(NavSearchContext);
  const { searchBarVariant } = context;

  const [parsedHistory, setParsedHistory] = useState<SearchHistoryType>({});
  const [history, setHistory] = useState<SearchEntry[]>([]);
  const contentClass = getContentClassByContentType(contentType);
  const header = contentClass.charAt(0).toUpperCase() + contentClass.slice(1);

  useEffect(() => {
    const storedHistory = localStorage.getItem('searchHistory');

    if (context?.enableSearchHistory && storedHistory) {
      try {
        const storedHistoryParsed = JSON.parse(storedHistory);
        const contentTypeSearched = storedHistoryParsed[contentClass] || [];
        setParsedHistory(storedHistoryParsed);
        setHistory(contentTypeSearched.reverse());
      } catch (e) {
        console.error('Invalid localStorage format:', e);
      }
    }
  }, [contentClass]);

  if (!context?.enableSearchHistory || history.length === 0) {
    return <>{children}</>;
  }

  return (
    <div className={searchBarVariantStyle[searchBarVariant] ?? 'py-2'}>
      <div className="flex justify-between items-center border-b border-gray-300">
        <h3 className="font-normal text-xs font-inter text-gray-900 m-0 mb-2">
          Recent {header} Searches
        </h3>
        <button
          className="bg-transparent border-none p-0 font-normal text-xs font-inter text-blue-700 m-0 mb-2"
          onClick={() => {
            setHistory([]);
            localStorage.removeItem('searchHistory');
          }}
        >
          Clear All
        </button>
      </div>
      <ul className="m-0 list-none">
        {history.map((searchEntry) => (
          <li className="pt-4" key={searchEntry?.term}>
            <div className="flex flex-row bg-blue-100 text-blue-900 text-base font-semibold font-inter rounded-lg py-1 px-2 w-fit items-center">
              <button
                className="bg-transparent border-none p-0 m-0 text-inherit font-inherit flex items-center"
                onClick={(e) => {
                  e.preventDefault();
                  const searchUrl = getSearchUrl({
                    contentType,
                    searchTerm: searchEntry?.term,
                    searchOrigin: searchOrigins.TYPE_AHEAD,
                  });
                  Telemetry.increment('typeahead.searchHistory.click');
                  window.location.href = searchUrl;
                }}
              >
                <div className="w-3 h-3 border-none mr-2 fill-current">
                  <TimeClockCircle />
                </div>
                {`"${searchEntry?.term}"`}
              </button>
              <button
                id="clear-search-term-button"
                type="button"
                onClick={(
                  e: React.MouseEvent<HTMLButtonElement, MouseEvent>
                ) => {
                  e.stopPropagation();
                  try {
                    setHistory((prev) =>
                      prev.filter(
                        (prevSearchEntry) =>
                          prevSearchEntry.term !== searchEntry?.term
                      )
                    );
                    parsedHistory[contentClass] = parsedHistory[
                      contentClass
                    ].filter(
                      (prevSearchEntry) =>
                        prevSearchEntry.term !== searchEntry?.term
                    );
                    localStorage.setItem(
                      'searchHistory',
                      JSON.stringify(parsedHistory)
                    );
                  } catch (e) {
                    console.error('Error updating history');
                  }
                }}
                className="w-3 h-3 border-none ml-2 fill-current"
              >
                <CloseIcon />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default SearchHistory;
